/**
 * Single source of truth for lazy()-wrapped page imports.
 *
 * Shared across the per-area route modules so each route file can stay
 * focused on path → component mapping without re-declaring imports.
 */
import { lazyPage } from "@/lib/lazyPage";

// ---------------- Customer-facing ----------------
export const Profile = lazyPage(() => import("@/pages/Profile"));
export const Subscription = lazyPage(() => import("@/pages/Subscription"));
export const SubscriptionSuccess = lazyPage(() => import("@/pages/SubscriptionSuccess"));
export const Unsubscribe = lazyPage(() => import("@/pages/Unsubscribe"));
export const ReportRedirect = lazyPage(() => import("@/pages/ReportRedirect"));
export const CoursePlayer = lazyPage(() => import("@/pages/CoursePlayer"));
export const CourseTrial = lazyPage(() => import("@/pages/CourseTrial"));
export const MembershipCheckout = lazyPage(() => import("@/pages/MembershipCheckout"));
export const Install = lazyPage(() => import("@/pages/Install"));
export const Assessment = lazyPage(() => import("@/pages/Assessment"));
export const AssessmentStart = lazyPage(() => import("@/pages/AssessmentStart"));
export const AssessmentChild = lazyPage(() => import("@/pages/AssessmentChild"));
export const AssessmentParent = lazyPage(() => import("@/pages/AssessmentParent"));
export const AssessmentReport = lazyPage(() => import("@/pages/AssessmentReport"));
export const AssessmentSampleReport = lazyPage(() => import("@/pages/AssessmentSampleReport"));
export const LearningJourneys = lazyPage(() => import("@/pages/LearningJourneys"));
export const ParentDashboard = lazyPage(() => import("@/pages/ParentDashboard"));
export const ParentPinReset = lazyPage(() => import("@/pages/ParentPinReset"));
export const MeetCharacters = lazyPage(() => import("@/pages/MeetCharacters"));
export const MeetCharacter = lazyPage(() => import("@/pages/MeetCharacter"));
export const FutureReadiness = lazyPage(() => import("@/pages/FutureReadiness"));
export const ConfidenceCheck = lazyPage(() => import("@/pages/ConfidenceCheck"));
export const ParentAssessmentsIndex = lazyPage(() => import("@/pages/ParentAssessmentsIndex"));
export const ParentAssessment = lazyPage(() => import("@/pages/ParentAssessment"));
export const Refer = lazyPage(() => import("@/pages/Refer"));
export const Blog = lazyPage(() => import("@/pages/Blog"));
export const BlogPost = lazyPage(() => import("@/pages/BlogPost"));
export const Research = lazyPage(() => import("@/pages/Research"));
export const Glossary = lazyPage(() => import("@/pages/Glossary"));
export const Courses = lazyPage(() => import("@/pages/Courses"));
export const LearnIndex = lazyPage(() => import("@/pages/LearnIndex"));
export const LearnTopic = lazyPage(() => import("@/pages/LearnTopic"));
export const CompetitorCompare = lazyPage(() => import("@/pages/CompetitorCompare"));
export const WriteToFounder = lazyPage(() => import("@/pages/WriteToFounder"));
export const Showcase = lazyPage(() => import("@/pages/Showcase"));
export const LandingPage = lazyPage(() => import("@/pages/LandingPage"));
export const GoRedirect = lazyPage(() => import("@/pages/GoRedirect"));
export const AuthDebug = lazyPage(() => import("@/pages/AuthDebug"));

// ---------------- Policy ----------------
export const NotFound = lazyPage(() => import("@/pages/NotFound"));
export const TermsOfUse = lazyPage(() => import("@/pages/TermsOfUse"));
export const RefundPolicy = lazyPage(() => import("@/pages/RefundPolicy"));
export const CopyrightPolicy = lazyPage(() => import("@/pages/CopyrightPolicy"));
export const PrivacyPolicy = lazyPage(() => import("@/pages/PrivacyPolicy"));

// ---------------- Admin ----------------
export const AdminHome = lazyPage(() => import("@/pages/admin/AdminHome"));
export const CourseGenerator = lazyPage(() => import("@/pages/admin/CourseGenerator"));
export const CeoDashboard = lazyPage(() => import("@/pages/admin/CeoDashboard"));
export const PublishCheck = lazyPage(() => import("@/pages/admin/PublishCheck"));
export const UtmBuilder = lazyPage(() => import("@/pages/admin/UtmBuilder"));
export const UtmFunnel = lazyPage(() => import("@/pages/admin/UtmFunnel"));
export const CourseManagement = lazyPage(() => import("@/pages/admin/CourseManagement"));
export const CourseDetail = lazyPage(() => import("@/pages/admin/CourseDetail"));
export const UserManagement = lazyPage(() => import("@/pages/admin/UserManagement"));
export const SubscriptionManagement = lazyPage(() => import("@/pages/admin/SubscriptionManagement"));
export const RegionalPricingManagement = lazyPage(() => import("@/pages/admin/RegionalPricingManagement"));
export const PaymentHealth = lazyPage(() => import("@/pages/admin/PaymentHealth"));
export const PricingAudit = lazyPage(() => import("@/pages/admin/PricingAudit"));
export const PromoCodeManagement = lazyPage(() => import("@/pages/admin/PromoCodeManagement"));
export const CourseCategoryManagement = lazyPage(() => import("@/pages/admin/CourseCategoryManagement"));
export const CertificateManagement = lazyPage(() => import("@/pages/admin/CertificateManagement"));
export const ReceiptManagement = lazyPage(() => import("@/pages/admin/ReceiptManagement"));
export const PaymentReconciliation = lazyPage(() => import("@/pages/admin/PaymentReconciliation"));
export const DataAnalytics = lazyPage(() => import("@/pages/admin/DataAnalytics"));
export const Monitoring = lazyPage(() => import("@/pages/admin/Monitoring"));
export const EdgeCache = lazyPage(() => import("@/pages/admin/EdgeCache"));
export const ErrorTracking = lazyPage(() => import("@/pages/admin/ErrorTracking"));
export const MetaEventAudit = lazyPage(() => import("@/pages/admin/MetaEventAudit"));
export const WebhookEvents = lazyPage(() => import("@/pages/admin/WebhookEvents"));
export const CspReports = lazyPage(() => import("@/pages/admin/CspReports"));
export const OtpAttempts = lazyPage(() => import("@/pages/admin/OtpAttempts"));
export const MetaPlanEvents = lazyPage(() => import("@/pages/admin/MetaPlanEvents"));
export const ElevenLabsKeyManagement = lazyPage(() => import("@/pages/admin/ElevenLabsKeyManagement"));
export const LogoManagement = lazyPage(() => import("@/pages/admin/LogoManagement"));
export const AssessmentTraitManagement = lazyPage(() => import("@/pages/admin/AssessmentTraitManagement"));
export const AssessmentAgeBandManagement = lazyPage(() => import("@/pages/admin/AssessmentAgeBandManagement"));
export const AssessmentQuestionManagement = lazyPage(() => import("@/pages/admin/AssessmentQuestionManagement"));
export const AssessmentSettingsManagement = lazyPage(() => import("@/pages/admin/AssessmentSettingsManagement"));
export const AssessmentSessionManagement = lazyPage(() => import("@/pages/admin/AssessmentSessionManagement"));
export const LearningJourneyManagement = lazyPage(() => import("@/pages/admin/LearningJourneyManagement"));
export const ParentJourneyManagement = lazyPage(() => import("@/pages/admin/ParentJourneyManagement"));
export const DialogueCharacterManagement = lazyPage(() => import("@/pages/admin/DialogueCharacterManagement"));
export const CourseBackgroundManagement = lazyPage(() => import("@/pages/admin/CourseBackgroundManagement"));
export const CourseTestManagement = lazyPage(() => import("@/pages/admin/CourseTestManagement"));
export const LanguageManagement = lazyPage(() => import("@/pages/admin/LanguageManagement"));
export const ContentChecker = lazyPage(() => import("@/pages/admin/ContentChecker"));
export const TrashManagement = lazyPage(() => import("@/pages/admin/TrashManagement"));
export const BulkVoiceover = lazyPage(() => import("@/pages/admin/BulkVoiceover"));
export const ReferralManagement = lazyPage(() => import("@/pages/admin/ReferralManagement"));
export const FutureReadinessManagement = lazyPage(() => import("@/pages/admin/FutureReadinessManagement"));
export const ConfidenceCheckManagement = lazyPage(() => import("@/pages/admin/ConfidenceCheckManagement"));
export const ParentAssessmentsAdminIndex = lazyPage(() => import("@/pages/admin/ParentAssessmentsAdminIndex"));
export const ParentAssessmentManagement = lazyPage(() => import("@/pages/admin/ParentAssessmentManagement"));
export const FounderMessagesManagement = lazyPage(() => import("@/pages/admin/FounderMessagesManagement"));
export const LandingPageManager = lazyPage(() => import("@/pages/admin/LandingPageManager"));
export const InfluencerManagement = lazyPage(() => import("@/pages/admin/InfluencerManagement"));
export const SeoInsights = lazyPage(() => import("@/pages/admin/SeoInsights"));
export const VoiceoverQueue = lazyPage(() => import("@/pages/admin/VoiceoverQueue"));
export const CourseReviewPanel = lazyPage(() => import("@/pages/admin/CourseReviewPanel"));
export const CourseVersions = lazyPage(() => import("@/pages/admin/CourseVersions"));
export const MediaLibrary = lazyPage(() => import("@/pages/admin/MediaLibrary"));
export const CoachChatManagement = lazyPage(() => import("@/pages/admin/CoachChatManagement"));
export const VisitorQueriesManagement = lazyPage(() => import("@/pages/admin/VisitorQueriesManagement"));

// ---------------- Student ----------------
export const CoachChat = lazyPage(() => import("@/pages/CoachChat"));
export const OAuthConsent = lazyPage(() => import("@/pages/OAuthConsent"));

// ---------------- Partner (influencer) ----------------
export const PartnersApply = lazyPage(() => import("@/pages/PartnersApply"));
export const Partner = lazyPage(() => import("@/pages/Partner"));

